import { useCallback, useEffect, useRef, useState } from 'react';
import { CallSignalingClient } from '../utils/callSignaling';
import { ICE_SERVERS } from '../utils/webrtcConfig';

export type LiveViewerState = 'idle' | 'requesting' | 'busy' | 'connecting' | 'live' | 'error';
export type LiveViewerAction =
  | { type: 'start' } | { type: 'offer' } | { type: 'connected' }
  | { type: 'busy' } | { type: 'error' } | { type: 'stop' };

export function liveViewerReducer(state: LiveViewerState, action: LiveViewerAction): LiveViewerState {
  switch (action.type) {
    case 'stop': return 'idle';
    case 'start': return state === 'idle' || state === 'error' || state === 'busy' ? 'requesting' : state;
    case 'offer': return state === 'requesting' ? 'connecting' : state;
    case 'connected': return state === 'connecting' ? 'live' : state;
    case 'busy': return state === 'requesting' ? 'busy' : state;
    case 'error': return 'error';
    default: return state;
  }
}

const REQUEST_TIMEOUT_MS = 10_000;
const PING_INTERVAL_MS = 20_000;

export function useLiveViewer(targetDoorbellId: number) {
  const [state, setState] = useState<LiveViewerState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [micMuted, setMicMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const clientRef = useRef<CallSignalingClient | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localAudioRef = useRef<MediaStream | null>(null);
  const watchIdRef = useRef<string>('');
  const timersRef = useRef<{ req?: ReturnType<typeof setTimeout>; ping?: ReturnType<typeof setInterval> }>({});

  const dispatch = useCallback((a: LiveViewerAction) => setState((s) => liveViewerReducer(s, a)), []);

  // Non-dispatch teardown: releases every resource but leaves the state
  // machine alone, so callers can follow it with whatever action fits
  // (stop → idle, busy, error, ...). Nulling clientRef is what makes a
  // later start() work again (it early-returns while clientRef is set).
  const teardown = useCallback(() => {
    const client = clientRef.current;
    if (client && watchIdRef.current) {
      client.send({ type: 'watch-end', to: `kiosk:${targetDoorbellId}:live`, watchId: watchIdRef.current });
    }
    if (timersRef.current.req) { clearTimeout(timersRef.current.req); timersRef.current.req = undefined; }
    if (timersRef.current.ping) { clearInterval(timersRef.current.ping); timersRef.current.ping = undefined; }
    pcRef.current?.close();
    pcRef.current = null;
    localAudioRef.current?.getTracks().forEach((t) => t.stop());
    localAudioRef.current = null;
    client?.close();
    clientRef.current = null;
    watchIdRef.current = '';
  }, [targetDoorbellId]);

  const stop = useCallback(() => {
    teardown();
    dispatch({ type: 'stop' });
    setErrorMsg(null);
  }, [teardown, dispatch]);

  // Terminal failure: tear everything down (so a later start() is not a
  // no-op on the `if (clientRef.current) return` guard) then surface the
  // error state.
  const fail = useCallback((msg: string) => {
    stop();
    setErrorMsg(msg);
    dispatch({ type: 'error' });
  }, [stop, dispatch]);

  const start = useCallback(() => {
    if (clientRef.current) return;
    const watchId = crypto.randomUUID();
    watchIdRef.current = watchId;
    const to = `kiosk:${targetDoorbellId}:live`;
    const client = new CallSignalingClient('resident', 'Observador', `watch-${watchId}`);
    clientRef.current = client;
    dispatch({ type: 'start' });
    setErrorMsg(null);

    // Grab the viewer's mic so they can talk back to whoever's at the door.
    // Best-effort: if it fails, live view still works one-way (video only).
    // Kept as a promise so the offer handler can await it and add the
    // track BEFORE createAnswer (otherwise the viewer is never heard).
    const micPromise = navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((s) => {
        if (watchIdRef.current === watchId) {
          localAudioRef.current = s;
          return s;
        }
        s.getTracks().forEach((t) => t.stop());
        return null;
      })
      .catch(() => null);

    client.on('watch-busy', (msg) => {
      if (msg.watchId !== watchId) return;
      // Tear the client down so a later start() from 'busy' is not a
      // permanent no-op (start() early-returns while clientRef is set).
      teardown();
      dispatch({ type: 'busy' });
    });
    client.on('watch-error', (msg) => {
      if (msg.watchId !== watchId) return;
      fail(msg.reason === 'camera' ? 'A campainha não conseguiu abrir a câmera' : String(msg.reason || 'Erro'));
    });
    client.on('watch-offer', async (msg) => {
      if (msg.watchId !== watchId) return;
      if (timersRef.current.req) clearTimeout(timersRef.current.req);
      const pc = new RTCPeerConnection(ICE_SERVERS);
      pcRef.current = pc;
      pc.onicecandidate = (e) => {
        if (e.candidate) client.send({ type: 'watch-ice', to, watchId, candidate: e.candidate });
      };
      pc.ontrack = (e) => {
        if (videoRef.current) {
          videoRef.current.srcObject = e.streams[0];
          videoRef.current.play().catch(() => {});
        }
      };
      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') dispatch({ type: 'connected' });
        if (['failed', 'disconnected'].includes(pc.connectionState)) fail('Conexão perdida');
      };
      // Send the viewer's voice to the doorbell. Wait for the mic (it
      // resolves async and the offer can beat it), then add the track
      // before createAnswer so it's in the SDP.
      const mic = localAudioRef.current || (await micPromise);
      if (mic) mic.getAudioTracks().forEach((t) => pc.addTrack(t, mic));
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(msg.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        client.send({ type: 'watch-answer', to, watchId, sdp: answer });
        dispatch({ type: 'offer' });
        timersRef.current.ping = setInterval(() => client.send({ type: 'watch-ping', to, watchId }), PING_INTERVAL_MS);
      } catch {
        fail('Falha ao negociar vídeo');
      }
    });
    client.on('watch-ice', (msg) => {
      if (msg.watchId !== watchId || !pcRef.current) return;
      pcRef.current.addIceCandidate(new RTCIceCandidate(msg.candidate)).catch(() => {});
    });
    client.on('watch-end', (msg) => {
      if (msg.watchId === watchId) fail('A campainha encerrou a transmissão');
    });

    client.connect();
    // (re)envia o pedido assim que o socket abre - inclusive após reconexão
    client.onceOpen(() => client.send({ type: 'watch-request', to, watchId }));
    timersRef.current.req = setTimeout(() => {
      fail('A campainha não respondeu');
    }, REQUEST_TIMEOUT_MS);
  }, [dispatch, targetDoorbellId, fail, teardown]);

  const toggleMic = useCallback(() => {
    setMicMuted((prev) => {
      const next = !prev;
      localAudioRef.current?.getAudioTracks().forEach((t) => (t.enabled = !next));
      return next;
    });
  }, []);

  const toggleSpeaker = useCallback(() => {
    setSpeakerMuted((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = next;
      return next;
    });
  }, []);

  useEffect(() => () => stop(), [stop]);
  // reset mute flags whenever a fresh session starts
  useEffect(() => {
    if (state === 'requesting') {
      setMicMuted(false);
      setSpeakerMuted(false);
    }
  }, [state]);

  return { state, start, stop, videoRef, errorMsg, micMuted, speakerMuted, toggleMic, toggleSpeaker };
}
